import { Action, PayloadAction, ThunkAction, createSelector, createSlice } from '@reduxjs/toolkit';
import storage from 'redux-persist/lib/storage';
import { BASE_APP_QUICK_PATH_CONFIG_STORAGE_KEY, CONFIG_STORAGE_KEY } from 'src/app/common/constants';
import { Configuration } from '../types/config-type';
import _ from 'lodash';
import { RootState } from 'src/redux/store';
import { AgeValidation, FullRegionList, RegionIdType, SelectOption } from 'src/app/common/types';
import { examModesType } from '../../Recruitment/types/license-exam-types';

export type ConfigurationState = {
  data?: Configuration;
  globalFullRegionList: FullRegionList | null; // PHKL Custom
};

export const configPersistConfig = {
  key: 'config',
  storage,
};

const initialState: ConfigurationState = {
  data: undefined,
  globalFullRegionList: null, // PHKL Custom
};

type SetConfigAction = {
  payload: Configuration;
};

export type ConfigThunkAction = ThunkAction<void, ConfigurationState, unknown, Action<string>>;

export const configSlice = createSlice({
  name: 'config',
  initialState,
  reducers: {
    setConfig: (state: ConfigurationState, action: SetConfigAction) => {
      if (typeof window !== undefined) {
        window.localStorage.setItem(CONFIG_STORAGE_KEY, JSON.stringify(action.payload));
      }
      return {
        ...state,
        data: action.payload,
      };
    },
    setAppQuickPaths: (state: ConfigurationState, action: SetConfigAction) => {
      if (typeof window !== undefined) {
        window.localStorage.setItem(BASE_APP_QUICK_PATH_CONFIG_STORAGE_KEY, JSON.stringify(action.payload));
      }
      return {
        ...state,
        appQuickPaths: action.payload,
      };
    },
    // PHKL Custom -s
    setGlobalFullRegionList: (state: ConfigurationState, action: PayloadAction<FullRegionList>) => {
      return {
        ...state,
        globalFullRegionList: action.payload,
      };
    },
    // PHKL Custom -e
  },
});

// PHKL Custom -s
export const { setConfig, setAppQuickPaths, setGlobalFullRegionList } = configSlice.actions;

// PHKL Custom -s
const selectExamModes = (state: RootState) => _.get(state.config.data, 'Recruitment.examModes', []);

export const selectExamModeOptions = createSelector([selectExamModes], (examModes): SelectOption[] =>
  examModes.map((mode: examModesType) => ({
    label: mode.displayKey,
    value: mode.code,
  })),
);

export const selectExamModeMap = createSelector(
  [selectExamModes],
  (examModes): Record<string, string> =>
    Object.fromEntries((examModes as examModesType[]).map(({ code, displayKey }) => [code, displayKey])),
);

const selectRegionIdTypeConfig = (state: RootState) => _.get(state.config.data, 'Global.region.idType', {});

export const selectRegionIdTypes = createSelector([selectRegionIdTypeConfig], (idTypes): RegionIdType[] => {
  const entries = Object.entries(idTypes).filter(([, item]: [string, any]) =>
    item.module?.includes('CandidateWeb.ExamRegistration'),
  );

  const hasDefault = entries.some(([, item]: [string, any]) => item.isDefault);

  return entries.map(([key, item]: [string, any], index) => ({
    key,
    label: `Common.${key}`,
    ...item,
    errMsg: item.errMsg?.replaceAll(':', '.'),
    desc: item.desc?.replaceAll(':', '.'),
    isDefault: item.isDefault || (!hasDefault && index === 0),
  }));
});

export const selectAgeValidation = (state: RootState): AgeValidation | null =>
  _.get(state?.config?.data, 'Global.region.ageValidation', null);

export const selectGlobalFullRegionList = (state: RootState) => state.config.globalFullRegionList;

export const selectGlobalRegionOptions = createSelector([selectGlobalFullRegionList], (regions) =>
  _.values(regions ?? {}).map(({ code, displayName, displayNameKey, regex }) => ({
    key: displayNameKey,
    label: displayName,
    value: code,
    regex,
  })),
);
// PHKL Custom -e
